// ===== Packages =====

import styled                       from 'styled-components';

// ===== Constants =====

import {
    BODY_FONT_SIZE,
}                                   from '../../constants/generalConstants';
import FONT_TYPE                    from '../../constants/fontType';

interface ContainerProps {
    hasFailure: boolean,
    hasSuccess: boolean,
}
export const Container = styled.div<ContainerProps>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100vw;
    min-height: 100vh;
    min-height: 100svh;
    min-height: 100dvh;
    padding: ${`${7.5 * BODY_FONT_SIZE}px ${1.25 * BODY_FONT_SIZE}px ${2.5 * BODY_FONT_SIZE}px`};
    box-sizing: border-box;
    background: ${({ theme }) => theme.color.white};
`;

export const ContentContainer = styled.div<ContainerProps>`
    width: 100%;
    max-width: ${`${30 * BODY_FONT_SIZE}px`};
    background: ${({ hasFailure, hasSuccess, theme }) => {
        if (hasFailure) return theme.verascopeColor.red100;
        if (hasSuccess) return theme.verascopeColor.green100;
        return theme.verascopeColor.purple200;
    }};
    box-shadow: ${({ theme }) => theme.color.boxShadow400};
    padding: ${`${1.875 * BODY_FONT_SIZE}px`};

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        padding: ${`${1.25 * BODY_FONT_SIZE}px`};
    }
`;

export const LogomarkContainer = styled.div`
    width: 100%;
    max-width: ${`${7.5 * BODY_FONT_SIZE}px`};
    margin: 0 auto;
    margin-bottom: ${`${1.25 * BODY_FONT_SIZE}px`};
`;

export const StatusHeader = styled.h1`
    margin: 0;
    margin-bottom: ${`${0.9375 * BODY_FONT_SIZE}px`};
    font-family: ${FONT_TYPE.PRESS_START};
    font-size: 1em;
    line-height: 1.6em;
    color: ${({ theme }) => theme.color.white};
    text-align: center;
`;

interface StatusMessageProps {
    hasFailure: boolean,
    hasSuccess: boolean,
}
export const StatusMessage = styled.p<StatusMessageProps>`
    margin: 0 auto;
    margin-bottom: ${`${1.5 * BODY_FONT_SIZE}px`};
    align-self: center;
    max-width: 46ch;
    font-family: ${FONT_TYPE.PRESS_START};
    font-size: 0.6em;
    line-height: 1.6em;
    text-align: center;
    color: ${({ hasFailure, hasSuccess, theme }) => {
        if (hasFailure) return theme.verascopeColor.red300;
        if (hasSuccess) return theme.verascopeColor.green300;
        return theme.verascopeColor.purple500;
    }};
`;

export const FormContainer = styled.form`
    display: flex;
    flex-direction: column;
    gap: ${`${0.75 * BODY_FONT_SIZE}px`};
`;

export const ClaimDetailsContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${`${0.75 * BODY_FONT_SIZE}px`};
`;

export const ClaimDetailItem = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${`${0.25 * BODY_FONT_SIZE}px`};
    text-align: center;
`;

export const ClaimDetailLabel = styled.p`
    margin: 0;
    font-family: ${FONT_TYPE.PRESS_START};
    font-size: 0.55em;
    line-height: 1.6em;
    color: ${({ theme }) => theme.verascopeColor.purple500};
    text-transform: uppercase;
`;

export const ClaimDetailValue = styled.p`
    margin: 0;
    font-family: ${FONT_TYPE.PRESS_START};
    font-size: 0.6em;
    line-height: 1.6em;
    color: ${({ theme }) => theme.color.white};
`;

export const ActionContainer = styled.div`
    display: flex;
    justify-content: center;
    margin-top: ${`${0.75 * BODY_FONT_SIZE}px`};
`;

export const ClaimCode = styled.code`
    display: block;
    width: 100%;
    box-sizing: border-box;
    padding: ${`${0.875 * BODY_FONT_SIZE}px ${0.75 * BODY_FONT_SIZE}px`};
    border: ${`${0.125 * BODY_FONT_SIZE}px`} solid ${({ theme }) => theme.color.white};
    background: ${({ theme }) => theme.color.white};
    color: ${({ theme }) => theme.verascopeColor.purple300};
    font-family: ${FONT_TYPE.PRESS_START};
    font-size: 0.7em;
    line-height: 1.7em;
    text-align: center;
    word-break: break-word;
`;

export const ClaimCarouselContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    gap: ${`${0.75 * BODY_FONT_SIZE}px`};
`;

export const ClaimCarouselCard = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    box-sizing: border-box;
    gap: ${`${0.75 * BODY_FONT_SIZE}px`};
    padding: ${`${0.875 * BODY_FONT_SIZE}px ${0.75 * BODY_FONT_SIZE}px`};
    border: ${({ theme }) => `${0.1875 * BODY_FONT_SIZE}px solid ${theme.verascopeColor.orange200}`};
    background: rgba(250, 250, 250, 0.06);
    box-shadow: ${({ theme }) => `${0.3125 * BODY_FONT_SIZE}px ${0.3125 * BODY_FONT_SIZE}px 0 ${theme.verascopeColor.purple100}`};
`;

export const ClaimCarouselNavRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    gap: ${`${0.75 * BODY_FONT_SIZE}px`};
`;

interface ClaimCarouselNavButtonProps {
    customCursorActive: boolean,
}
export const ClaimCarouselNavButton = styled.button<ClaimCarouselNavButtonProps>`
    min-width: ${`${5.5 * BODY_FONT_SIZE}px`};
    padding: ${`${0.5 * BODY_FONT_SIZE}px ${0.75 * BODY_FONT_SIZE}px`};
    border: ${`${0.125 * BODY_FONT_SIZE}px`} solid ${({ theme }) => theme.color.white};
    background: transparent;
    color: ${({ theme }) => theme.color.white};
    font-family: ${FONT_TYPE.PRESS_START};
    font-size: 0.5em;
    line-height: 1.4em;
    cursor: ${({ customCursorActive }) => (customCursorActive ? 'none' : 'pointer')};

    &:hover:not(:disabled) {
        background: ${({ theme }) => theme.color.white};
        color: ${({ theme }) => theme.verascopeColor.purple300};
    }

    &:disabled {
        opacity: 0.4;
        cursor: default;
    }
`;

export const ClaimCarouselDots = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${`${0.5625 * BODY_FONT_SIZE}px`};
`;

interface ClaimCarouselDotButtonProps {
    active: boolean,
    customCursorActive: boolean,
}
export const ClaimCarouselDotButton = styled.button<ClaimCarouselDotButtonProps>`
    position: relative;
    width: ${`${0.75 * BODY_FONT_SIZE}px`};
    height: ${`${0.75 * BODY_FONT_SIZE}px`};
    padding: 0;
    border: ${({ active, theme }) => `${
        0.1875 * BODY_FONT_SIZE
    }px solid ${
        active ? theme.verascopeColor.orange200 : theme.color.white
    }`};
    background: ${({ active, theme }) => (
        active ? theme.verascopeColor.orange200 : 'transparent'
    )};
    cursor: ${({ customCursorActive }) => (customCursorActive ? 'none' : 'pointer')};
    box-sizing: content-box;

    &::after {
        content: "";
        position: absolute;
        top: 50%;
        left: 50%;
        width: ${`${0.375 * BODY_FONT_SIZE}px`};
        height: ${`${0.375 * BODY_FONT_SIZE}px`};
        transform: translate(-50%, -50%);
        background: ${({ active, theme }) => (active ? theme.color.white : 'transparent')};
    }

    &:hover {
        border-color: ${({ active, theme }) => (active ? theme.verascopeColor.orange200 : theme.verascopeColor.orange300)};
    }
`;

export const PasswordStrengthContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: ${`${0.375 * BODY_FONT_SIZE}px`};
    width: 100%;
    padding-top: ${`${0.3125 * BODY_FONT_SIZE}px`};
`;

interface PasswordStrengthBarProps {
    active: boolean,
    hasErrors: boolean,
}
export const PasswordStrengthBar = styled.div<PasswordStrengthBarProps>`
    flex: 1;
    height: ${`${0.4375 * BODY_FONT_SIZE}px`};
    border-radius: 0;
    background: ${({ active, hasErrors, theme }) => {
        if (hasErrors && active) return theme.verascopeColor.red200;
        if (active) return theme.verascopeColor.blue200;
        return theme.color.neutral300;
    }};
`;

export const LoadingContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: ${`${6.25 * BODY_FONT_SIZE}px`};
`;
